import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { lowerCase } from 'lodash';
import { BiGhost } from 'react-icons/bi';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { Profile } from '@par/shared/types/player';
import { resizeImage } from '../../api/cloudinary';
import { WhosOnline } from '../room/types';
import { SearchDrawer } from './SearchDrawer';
import { PlayerActivityStatus } from './PlayerActivityStatus';
import { DrawerButtonWhosOnline } from './DrawerButtonWhosOnline';
import { FilterBadge } from './FilterBadge';
import { getAvatarSrc } from '../room/utils';

export type MenuItemProps = {
  label: string;
  filterOn: string;
  filterValue: string | boolean;
};

type Props = {
  onlinePlayerCount: number;
  openDrawer: boolean;
  profile: Profile;
  whosOnline: WhosOnline[];
  handleGhostChallenge: (player: WhosOnline) => void;
  setOpenDrawer: (isOpen: boolean) => void;
};

export const MessagesDrawer = ({
  onlinePlayerCount,
  openDrawer,
  profile,
  whosOnline,
  handleGhostChallenge,
  setOpenDrawer,
}: Props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  const [selectedMenuItem, setSelectedMenuItem] =
    useState<MenuItemProps | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleAvatarClick = (event) => {
    event.stopPropagation();
    navigate(`/profile/overview/${profile.id}`);
  };

  const handleGhostChallengeClick = (player: WhosOnline) => {
    if (player.ghostChallengeStatus === 'no') return;
    handleGhostChallenge(player);
  };

  const handlePlayerClick = (profileId: string) => {
    navigate(`/profile/overview/${profileId}`);
  };

  const ghostChallengeStatusTooltip = (status: 'yes' | 'no' | 'maybe') => {
    let message = 'May or may not respond to challenges';
    if (status === 'yes') {
      message = 'Send me challenges!';
    }
    if (status === 'no') {
      message = 'Not accepting challenges right now';
    }
    return <Typography variant="caption">Status: {message}</Typography>;
  };

  const sortUsers = (whosOnline?: WhosOnline[]) => {
    if (!whosOnline) return [];
    const onlineUsers = whosOnline
      .filter((u) => u.online && !u.isIdle)
      .sort((a, b) => b.loggedInAt - a.loggedInAt);
    const idleUsers = whosOnline
      .filter((u) => u.online && u.isIdle)
      .sort((a, b) => b.loggedInAt - a.loggedInAt);
    const offlineUsers = whosOnline
      .filter((u) => !u.online)
      .sort((a, b) => b.disconnectedAt - a.disconnectedAt);
    return onlineUsers.concat(idleUsers).concat(offlineUsers);
  };

  const filterSearch = (whosOnline: WhosOnline[]) => {
    if (!searchTerm) return whosOnline;
    return whosOnline.filter((el) =>
      lowerCase(el.username).includes(lowerCase(searchTerm))
    );
  };

  const filteredUsers = (whosOnline?: WhosOnline[]) => {
    if (!selectedMenuItem) return sortUsers(whosOnline);
    return sortUsers(whosOnline).filter(
      (el) => el[selectedMenuItem.filterOn] === selectedMenuItem.filterValue
    );
  };

  return (
    <>
      {openDrawer ? (
        <DrawerButtonWhosOnline
          openMessagesDrawer={openDrawer}
          profile={profile}
          setOpenMessagesDrawer={setOpenDrawer}
          onlinePlayerCount={onlinePlayerCount}
          whosOnline={whosOnline}
        />
      ) : null}

      <SearchDrawer
        setSearchTerm={setSearchTerm}
        setSelectedMenuItem={setSelectedMenuItem}
        selectedMenuItem={selectedMenuItem}
      />

      <FilterBadge
        selectedMenuItem={selectedMenuItem}
        setSelectedMenuItem={setSelectedMenuItem}
      />

      <List sx={{ overflow: 'auto' }}>
        {filterSearch(filteredUsers(whosOnline))
          .filter((p) => p.profileId !== profile.id)
          .slice(0, 80)
          .map((player, index) => (
            <Box key={player.profileId}>
              <ListItem
                disablePadding
                disableGutters
                secondaryAction={
                  <Tooltip
                    title={ghostChallengeStatusTooltip(
                      player.ghostChallengeStatus
                    )}
                    placement="top"
                  >
                    <IconButton
                      onClick={() => handleGhostChallengeClick(player)}
                    >
                      <BiGhost
                        style={{
                          color:
                            player.ghostChallengeStatus === 'yes'
                              ? theme.palette.underPar.main
                              : player.ghostChallengeStatus === 'no'
                              ? 'gray'
                              : theme.palette.primary.main,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemButton
                  onClick={() => handlePlayerClick(player?.profileId)}
                  key={index}
                  sx={{ paddingRight: '16px !important', paddingLeft: 1 }}
                >
                  <ListItemAvatar sx={{ minWidth: 40, marginRight: 1 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      badgeContent={
                        player.isMobile ? (
                          <PhoneAndroidIcon
                            sx={{
                              background: theme.palette.secondary.main,
                              padding: '3px',
                              borderRadius: '16px',
                            }}
                            fontSize="small"
                          />
                        ) : null
                      }
                    >
                      <Avatar
                        alt={player?.username}
                        src={getAvatarSrc(
                          player?.avatarPublicId,
                          player?.avatarUrl,
                          100,
                          100
                        )}
                        sx={{
                          filter: player.online ? '' : 'grayscale(100%)',
                        }}
                      />
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={player?.username}
                    primaryTypographyProps={{ fontSize: 14 }}
                    secondary={<PlayerActivityStatus player={player} />}
                    sx={{ margin: 0 }}
                  />
                </ListItemButton>
              </ListItem>
              <Divider variant="inset" component="li" sx={{ marginLeft: 7 }} />
            </Box>
          ))}
      </List>
    </>
  );
};
